import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * App-wide crash boundary. Catches render/lifecycle errors anywhere below it so
 * a bug in one screen shows a friendly message instead of a blank page — the
 * real error/stack is logged to the console for debugging, never rendered.
 * Must be a class component — no hook equivalent exists for componentDidCatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center p-6 text-center"
          style={{ background: 'var(--theme-background, #FDFBF7)' }}
          data-testid="error-boundary-screen"
        >
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 bg-red-50">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--theme-text-main, #1A1D1A)' }}>
            Something went wrong
          </h1>
          <p className="text-sm max-w-xs mb-6" style={{ color: 'var(--theme-text-muted, #5C635A)' }}>
            Please try again. If the problem continues, contact support.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{ background: 'var(--theme-primary, #2E3B32)' }}
            data-testid="error-boundary-reload-btn"
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
